"use client";

import React from "react";
import moment, { Moment } from "moment";
import { message } from "antd";
import {
  exportToExcelService,
  programsStudentAcademy,
  studentProgramAssistancesAcademy,
} from "../../../../shared/services";
import { AttendanceStatus } from "../../../../types/dto/program";

export interface AttendanceReportRow {
  programStudentId: number;
  userId: number;
  userName: string;
  studentCode?: string;
  totalClasses: number;
  present: number;
  absent: number;
  attendancePercent: number;
}

export default function useProgramAttendanceReport(
  programId: number,
  month: Moment
) {
  const [loading, setLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [rows, setRows] = React.useState<AttendanceReportRow[]>([]);

  const load = React.useCallback(async () => {
    if (!programId) return;
    setLoading(true);

    try {
      const monthStart = month.clone().startOf("month");
      const monthEnd = month.clone().endOf("month");

      const [studentsRes, assistancesRes] = await Promise.all([
        programsStudentAcademy.find({
          query: { program_id: programId, $limit: 10000, $sort: { id: -1 } },
        }),
        studentProgramAssistancesAcademy.find({
          query: { program_id: programId, $limit: 10000 },
        }),
      ]);

      const students = Array.isArray(studentsRes)
        ? studentsRes
        : studentsRes.data ?? [];

      const assistances = Array.isArray(assistancesRes)
        ? assistancesRes
        : assistancesRes.data ?? [];

      const studentMap = new Map<number, AttendanceReportRow>();
      for (const student of students) {
        const userName = `${student.user?.first_name ?? ""} ${
          student.user?.last_name ?? ""
        }`.trim();
        studentMap.set(student.id, {
          programStudentId: student.id,
          userId: student.user_id,
          userName: userName || `Estudiante #${student.user_id}`,
          studentCode: student.user?.student_code,
          totalClasses: 0,
          present: 0,
          absent: 0,
          attendancePercent: 0,
        });
      }

      for (const record of assistances) {
        const recordDate = moment(record.assistance_date);
        if (!recordDate.isBetween(monthStart, monthEnd, "day", "[]")) {
          continue;
        }

        const row = studentMap.get(record.program_student_id);
        if (!row) continue;

        row.totalClasses += 1;
        const status = record.attendance_status as AttendanceStatus | undefined;
        if (status === "absent") {
          row.absent += 1;
        } else {
          row.present += 1;
        }
      }

      const reportRows: AttendanceReportRow[] = Array.from(studentMap.values())
        .map((row) => ({
          ...row,
          attendancePercent:
            row.totalClasses > 0
              ? Math.round((row.present / row.totalClasses) * 100)
              : 0,
        }))
        .sort((a, b) => a.userName.localeCompare(b.userName, "es"));

      setRows(reportRows);
    } catch (err) {
      console.error("Error al cargar reporte de asistencia:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [programId, month]);

  React.useEffect(() => {
    load();
  }, [load]);

  const exportToExcel = async () => {
    if (!rows.length) {
      message.warning("No hay datos para exportar");
      return;
    }

    setExporting(true);
    try {
      const records = rows.map((row) => ({
        Estudiante: row.userName,
        Codigo: row.studentCode ?? "",
        "Total clases": row.totalClasses,
        Asistencias: row.present,
        Inasistencias: row.absent,
        "Porcentaje asistencia": `${row.attendancePercent}%`,
      }));

      const result = await exportToExcelService.create({
        records,
        folder: "academy-attendance",
        fileName: `asistencia-programa-${programId}-${month.format("YYYY-MM")}`,
        worksheetName: `Asistencia ${month.format("MMMM YYYY")}`,
      });

      if (result?.objectUrl) {
        const link = document.createElement("a");
        link.href = result.objectUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success("Reporte exportado a Excel");
      } else {
        message.error("No se pudo generar el archivo Excel");
      }
    } catch (err: any) {
      message.error(err?.message ?? "Error al exportar a Excel");
    } finally {
      setExporting(false);
    }
  };

  return { loading, exporting, rows, load, exportToExcel };
}
